import dayjs from "dayjs";
import { useMemo } from "react";
import { StyledDatePicker, StyledRangePicker } from "./styles";
import { isNonEmptyVariable } from "@/util/common_function";

const CommonDatePicker = ({
  value,
  onChange,
  placeholder = "Select date",
  format = "MM/DD/YYYY HH:mm",
  disabled = false,
  allowClear = true,
  disableFutureDates = false,
  fromDateDisable = "",
  range = false,
  showTime = true,
  isDOB = false,
  disablePastDate = false,
  onKeyDown,
  ...rest
}) => {
  const disabledDate = (current) => {
    if (isDOB) {
      const earliest = dayjs("1900-01-01");
      const latest = dayjs();
      return current && (current < earliest || current > latest.endOf("day"));
    }

    if (disablePastDate) {
      return current < dayjs().startOf("day");
    }

    if (isNonEmptyVariable(fromDateDisable)) {
      return current && current < dayjs(fromDateDisable).endOf("day");
    } else {
      if (!disableFutureDates) return false;
      return current && current > dayjs().endOf("day");
    }
  };

  const PickerComponent = useMemo(
    () => (range ? StyledRangePicker : StyledDatePicker),
    [range]
  );

  // antd/rc-picker's internal validity check (useFieldsInvalidate) calls
  // `date.isValid()` straight on whatever `value` holds, with no guard of
  // its own — any caller that leaks a raw string/number instead of a dayjs
  // instance (e.g. a date that failed to parse upstream) crashes the whole
  // page with "date.isValid is not a function", past any try/catch since it
  // happens inside antd's render. Safe here instead of trusting every call
  // site across the app to always convert correctly.
  const toSafeValue = (v) => (dayjs.isDayjs(v) ? v : null);
  const safeValue = Array.isArray(value) ? value.map(toSafeValue) : toSafeValue(value);

  // Alt+D fills in today's date, Alt+T fills in the current time. (Plain
  // Ctrl+T and Ctrl+Shift+T can't be used - Chrome/Firefox reserve them for
  // "open new tab" / "reopen closed tab" and never let page JS intercept
  // them; Ctrl+D was switched to Alt+D to match.) On a combined date+time
  // field each shortcut only touches its own part of the value, leaving
  // whatever the other part already holds untouched. Range pickers are left
  // alone - "current date/time" doesn't map cleanly onto a start/end pair.
  // Wired directly onto this component (not a wrapper) since Form.Item
  // clones only its direct child to bind value/onChange.
  const handleKeyDown = (e) => {
    if (!disabled && !range) {
      const key = e.key?.toLowerCase();
      const isDateKey =
        key === "d" && e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey;
      const isTimeKey =
        key === "t" && e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey && showTime;

      if (isDateKey || isTimeKey) {
        e.preventDefault();
        const now = dayjs();
        const base = dayjs.isDayjs(value) ? value : now;
        const next = isDateKey
          ? base.year(now.year()).month(now.month()).date(now.date())
          : base.hour(now.hour()).minute(now.minute()).second(now.second());
        onChange?.(next, next.format(format));
      }
    }
    onKeyDown?.(e);
  };

  return (
    <PickerComponent
      width="100%"
      value={safeValue}
      onChange={onChange}
      placeholder={placeholder}
      format={{
        format: format,
        type: "mask",
      }}
      disabled={disabled}
      allowClear={allowClear}
      disabledDate={disabledDate}
      showTime={showTime}
      onKeyDown={handleKeyDown}
      {...rest}
    />
  );
};

export default CommonDatePicker;
